import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

/**
 * Defense-in-depth gate for every /admin/* route.
 *
 * Authoritative access control still lives in Postgres (RLS + the
 * `has_role(uid, 'admin')` check). This wrapper exists so that a newly
 * added admin page cannot accidentally ship without a session/role check
 * — wrap once in `App.tsx` and every admin chunk is protected.
 *
 * Behavior:
 *  - No session → redirect to /admin/auth?redirect=<original path>
 *  - Signed in but not admin → redirect to /admin/auth (RLS will block
 *    data access either way; we hide the UI to avoid leaking shape).
 *  - Admin → render children.
 */
export function RequireAdmin({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [state, setState] = useState<"checking" | "allow" | "deny">(
    "checking",
  );

  useEffect(() => {
    let mounted = true;

    const check = async (uid: string | undefined) => {
      if (!uid) {
        if (mounted) setState("deny");
        return;
      }
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", uid)
        .eq("role", "admin")
        .maybeSingle();
      if (!mounted) return;
      if (error || !data) {
        setState("deny");
        return;
      }
      setState("allow");
    };

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      // Defer to avoid the documented deadlock when calling supabase
      // inside the auth listener callback.
      setTimeout(() => check(session?.user?.id), 0);
    });

    void supabase.auth.getSession().then(({ data }) => {
      check(data.session?.user?.id);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  if (state === "checking") {
    return <div className="min-h-screen bg-background" aria-hidden="true" />;
  }
  if (state === "deny") {
    const redirect = `${location.pathname}${location.search}`;
    const params = new URLSearchParams({ redirect });
    return <Navigate to={`/admin/auth?${params.toString()}`} replace />;
  }
  return <>{children}</>;
}